import type { Vero1Severity } from "shared/interfaces/api/vero1";
import styled, { css, keyframes } from "styled-components";

/* Spec §04 drawn grammar (Spec.dc.html drawings 4.1 / 4.3 / 4.5), token
   values resolved from the DS (colors_and_type.css): primary-400 #2D2943,
   text-muted #787395, text-faint #B2AFC8, border #E8E7EE, neutral-300
   #EEEDF2, secondary-600 #6841BA. */

export const INK = "#2D2943";
export const MUTED = "#787395";
export const FAINT = "#B2AFC8";
export const BORDER = "#E8E7EE";
export const DIVIDER = "#EEEDF2";
export const VIOLET = "#6841BA";

/* 4.1: section card — white, 1px border, 6px radius; cards stack at 18px. */
export const SectionCard = styled.section`
  background: #ffffff;
  border: 1px solid ${BORDER};
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 18px;
`;

export const HeaderPad = styled.div`
  padding: 14px 18px 0;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

export const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

export const SectionTitle = styled.span`
  font: 500 14px "Work Sans", sans-serif;
  color: ${INK};
`;

/* the header selector chip: 24px tall, 3px radius, 11.5px */
export const HeaderSelect = styled.select`
  height: 24px;
  padding: 0 9px;
  border: 1px solid ${BORDER};
  border-radius: 3px;
  font: 400 11.5px "Work Sans", sans-serif;
  color: ${INK};
  background: #ffffff;
  max-width: 320px;

  &:focus-visible {
    outline: none;
    border-color: #8052d0;
    box-shadow: 0 0 0 3px rgba(128, 82, 208, 0.18);
  }
`;

export const FaintNote = styled.span`
  font: 300 11px "Work Sans", sans-serif;
  color: ${FAINT};
  white-space: nowrap;
`;

export const HeaderMeta = styled.span`
  font: 300 11.5px "Work Sans", sans-serif;
  color: ${MUTED};
`;

export const MetaLine = styled.div`
  font: 300 11.5px "Work Sans", sans-serif;
  color: ${MUTED};
  margin-top: 3px;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

/* .chip — 22px, 2px radius, 500 11px */
export const RowName = styled.div`
  flex: 1;
  font: 500 13px "Work Sans", sans-serif;
  color: ${INK};
  min-width: 0;
`;

export const RowApplicant = styled.span`
  font: 300 11.5px "Work Sans", sans-serif;
  color: ${MUTED};
  margin-left: 6px;
`;

export const RowFindings = styled.p<{ $indent?: number }>`
  font: 300 11.5px/1.5 "Work Sans", sans-serif;
  color: ${MUTED};
  margin: 2px 0 0 ${({ $indent }) => $indent ?? 0}px;
  max-width: 88ch;
`;

export const SEVERITY_CHIP_TONE: Record<Vero1Severity, string> = {
  pass: "APPROVE",
  warn: "CONDITIONAL",
  fail: "DECLINE",
};

const CHIP_TONES: Record<string, { bg: string; fg: string }> = {
  APPROVE: { bg: "#D8F3EA", fg: "#03AB70" },
  AGREE: { bg: "#D8F3EA", fg: "#03AB70" },
  CONDITIONAL: { bg: "#FFE4CC", fg: "#B35500" },
  DECLINE: { bg: "#FAE4E9", fg: "#B92443" },
  CANCEL: { bg: "#E8E7EE", fg: "#2D2943" },
};

export const VerdictChip = styled.span<{ $tone?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 9px;
  border-radius: 2px;
  font: 500 11px "Work Sans", sans-serif;
  line-height: 1;
  ${({ $tone }) => {
    const tone = CHIP_TONES[$tone ?? ""] ?? { bg: "#F4F3F8", fg: INK };
    return css`
      background: ${tone.bg};
      color: ${tone.fg};
    `;
  }}
`;

/* 4.1: the 2px rule under each section header, colored by verdict */
const RULE_TONES: Record<string, string> = {
  APPROVE: "#56C79A",
  AGREE: "#56C79A",
  CONDITIONAL: "#F2B357",
  DECLINE: "#E26982",
  CANCEL: "#DCD9E8",
};

export const VerdictRule = styled.div<{ $tone?: string }>`
  height: 2px;
  margin-top: 11px;
  background: ${({ $tone }) => RULE_TONES[$tone ?? ""] ?? "#DCD9E8"};
`;

/* .btnk.ghost at the header's 30px/12px size */
export const GhostButton = styled.button`
  font: 500 12px "Work Sans", sans-serif;
  height: 30px;
  padding: 0 16px;
  border-radius: 2px;
  border: 1px solid ${INK};
  background: #ffffff;
  color: ${INK};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(128, 82, 208, 0.18);
  }
`;

export const SectionBody = styled.div<{ $muted?: boolean }>`
  padding: 6px 18px 16px;
  ${({ $muted }) =>
    $muted &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

/* .grouphd */
export const GroupHeading = styled.h4<{ $first?: boolean }>`
  font: 600 10px "Work Sans", sans-serif;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${MUTED};
  margin: ${({ $first }) => ($first ? "14px" : "16px")} 0 2px;
`;

export const GroupHeadingSub = styled.span`
  font: 400 10px "Work Sans", sans-serif;
  letter-spacing: 0;
  text-transform: none;
  color: ${FAINT};
`;

export const ChipRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 18px 0;
`;

export const FilterChip = styled.button<{ $active?: boolean }>`
  font: 500 11px "Work Sans", sans-serif;
  height: 22px;
  padding: 0 10px;
  border-radius: 2px;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? INK : BORDER)};
  background: ${({ $active }) => ($active ? INK : "#ffffff")};
  color: ${({ $active }) => ($active ? "#ffffff" : INK)};

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(128, 82, 208, 0.18);
  }
`;

export const FilteredEmptyLine = styled.p`
  margin: 16px 0 4px;
  font: 300 12.5px "Work Sans", sans-serif;
  color: ${MUTED};
`;

/* 4.1: the inline internal strip — a gold inset card under the header */
export const InTabStrip = styled.div`
  margin: 12px 18px 0;
  border: 1px solid #e2cfa6;
  background: #fcf7ec;
  border-radius: 4px;
  padding: 10px 13px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const GoldDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #b8860b;
  flex: 0 0 auto;
`;

export const StripText = styled.span`
  font: 400 12.5px "Work Sans", sans-serif;
  color: #6b531c;
`;

export const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 2px;
`;

/* .newlum */
export const NewResultBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 21px;
  padding: 0 8px;
  border-radius: 2px;
  background: #f6efe0;
  border: 1px solid #e2cfa6;
  font: 500 11px "Work Sans", sans-serif;
  color: #8a6d28;
  white-space: nowrap;
  cursor: pointer;
`;

/* 4.1: the nested run card under an affected row */
export const RunCardNested = styled.div`
  margin: 11px 0 2px 37px;
  border: 1px solid #e2cfa6;
  border-radius: 4px;
  background: #ffffff;
  overflow: hidden;
`;

export const RunCardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 12px;
  background: #fcf7ec;
  border-bottom: 1px solid #efe3cb;
`;

export const RunCardLabel = styled.span`
  font: 600 9.5px "Work Sans", sans-serif;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a6d28;
`;

export const RunCardBody = styled.div`
  padding: 11px 13px;
`;

export const RunCardText = styled.p`
  font: 300 12px/1.6 "Work Sans", sans-serif;
  color: ${INK};
  margin: 0;
  max-width: 74ch;
`;

export const RunCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 11px;
`;

export const RunCardHint = styled.span`
  font: 300 11px "Work Sans", sans-serif;
  color: ${MUTED};
`;

export const EmptyState = styled.div`
  padding: 34px 24px 38px;
  text-align: center;
`;

export const EmptyTitle = styled.h3`
  margin: 0 0 6px;
  font: 500 14px "Work Sans", sans-serif;
  color: ${INK};
`;

export const EmptyBody = styled.p`
  margin: 0 auto 14px;
  max-width: 46ch;
  font: 300 12.5px "Work Sans", sans-serif;
  line-height: 1.6;
  color: ${MUTED};
`;

export const RationaleBlock = styled.div`
  padding: 14px 18px 0;
`;

export const RationaleLabel = styled.div`
  font: 600 10px "Work Sans", sans-serif;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${MUTED};
  margin-bottom: 6px;
`;

export const RationaleText = styled.p`
  margin: 0;
  max-width: 88ch;
  font: 300 13px "Work Sans", sans-serif;
  line-height: 1.6;
  color: ${INK};
`;

/* 4.3: the in-progress run line replacing the selector */
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const RunProgressChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 24px;
  padding: 0 10px;
  border: 1px solid #c9c4dc;
  border-radius: 3px;
  font: 400 11.5px "Work Sans", sans-serif;
  color: ${MUTED};
  background: #f7f6fb;
`;

export const RunProgressSpinner = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 2px solid #b4aecd;
  border-top-color: ${VIOLET};
  display: inline-block;
  animation: ${spin} 0.9s linear infinite;
`;

/* height reservation while a section's data is in flight — the scroll-into-
   view from the banner anchors against the settled layout, not a half-mounted
   one, and the late rows can't shove the Lumen card down the page */
const shimmer = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
`;

export const SkeletonBar = styled.div`
  height: 15px;
  border-radius: 3px;
  margin: 18px 0;
  background: linear-gradient(90deg, #f4f3f8 25%, #ecebf2 50%, #f4f3f8 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease infinite;

  &:nth-child(2n) {
    width: 72%;
  }
`;

/* the review is on hold — the leasing read of the 4.1 strip: the same band
   grammar, on the DS neutrals (neutral-300 ground, border, text-muted dot,
   primary ink) instead of the internal-only gold */
export const HoldStrip = styled.div`
  margin: 12px 18px 0;
  border: 1px solid ${BORDER};
  background: ${DIVIDER};
  border-radius: 4px;
  padding: 10px 13px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const HoldDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${MUTED};
  flex: 0 0 auto;
`;

export const HoldText = styled.span`
  font: 400 12.5px "Work Sans", sans-serif;
  color: ${INK};
`;
